import { ComponentProps, KeyboardEvent, MouseEvent, useCallback } from 'react';

import { twMerge } from 'tailwind-merge';

import { Icon, IconNames } from '../Icon';

const COLORS = {
  NEUTRAL: 'neutral',
  GREEN: 'green',
  RED: 'red',
  YELLOW: 'yellow',
} as const;

type BadgeColor = (typeof COLORS)[keyof typeof COLORS];

const SIZES = {
  XS: 'xs',
  SM: 'sm',
  MD: 'md',
  LG: 'lg',
} as const;

type BadgeSize = (typeof SIZES)[keyof typeof SIZES];

type BadgeProps = {
  readonly text?: string;
  readonly icon?: IconNames;
  readonly color: BadgeColor;
  readonly size?: BadgeSize;
  readonly type?: 'normal' | 'invert';
  readonly onClick?: (e?: MouseEvent<HTMLSpanElement>) => void;
  readonly className?: React.HTMLAttributes<HTMLSpanElement>['className'];
};

type BadgeStyle = {
  [size: string]: {
    bg: string;
    icon: ComponentProps<typeof Icon>['color'];
    text: string;
  };
};

const SIZE_STYLE = {
  xs: 'text-[8px] min-h-[12px] leading-[12px] pb-[2px] px-[6px]',
  sm: 'text-[12px] min-h-[18px] leading-[18px] pb-[2px] px-[6px]',
  md: 'text-[12px] min-h-[18px] leading-[18px] py-[3px] px-[8px]',
  lg: 'text-[14px] min-h-[24px] leading-[24px] py-[4px] px-[12px]',
};

const WITH_ICON_STYLE = {
  xs: 'pl-[2px]',
  sm: 'pl-[4px]',
  md: 'pl-[6px]',
  lg: 'pl-[8px]',
};

const STYLE: { [type: string]: BadgeStyle } = {
  normal: {
    neutral: {
      bg: 'bg-neutral-500',
      icon: 'white',
      text: 'text-white',
    },
    green: {
      bg: 'bg-green-500',
      icon: 'white',
      text: 'text-white',
    },
    yellow: {
      bg: 'bg-yellow-500',
      icon: 'white',
      text: 'text-white',
    },
    red: {
      bg: 'bg-red-500',
      icon: 'white',
      text: 'text-white',
    },
  },
  invert: {
    neutral: {
      icon: 'neutral',
      bg: 'bg-neutral-100',
      text: 'text-neutral-500',
    },
    green: {
      icon: 'green',
      bg: 'bg-green-100',
      text: 'text-green-500',
    },
    yellow: {
      icon: 'yellow',
      bg: 'bg-yellow-100',
      text: 'text-yellow-500',
    },
    red: {
      icon: 'red',
      bg: 'bg-red-100',
      text: 'text-red-500',
    },
  },
};

function Badge({ text, icon, color, size = 'lg', type = 'normal', onClick, className }: BadgeProps) {
  const styling = STYLE[type][color];
  const iconColor = styling.icon;
  const bgColor = styling.bg;
  const textColor = styling.text;

  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        onClick?.();
      }
    },
    [onClick],
  );

  return (
    <span
      data-testid="badge"
      tabIndex={0}
      role="button"
      onClick={onClick}
      onKeyUp={onKeyUpHandler}
      className={twMerge(
        'inline-flex h-min items-center gap-1 rounded-full',
        bgColor,
        textColor,
        SIZE_STYLE[size],
        icon && WITH_ICON_STYLE[size],
        className,
      )}
    >
      {icon && <Icon icon={icon} color={iconColor} size={size} />}
      {text && <span>{text}</span>}
    </span>
  );
}

export { Badge };
